import { useMemo, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Switch } from '@components/Switch';
import { Typography } from '@components/Typography';
import { Select } from '@components/Select';
import { useTranslation, useSurveysOptions } from '@hooks';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';

const Survey = ({ isView, isMobile }) => {
    const { t } = useTranslation();
    const { control, setValue, getValues, watch } = useFormContext();
    const { getSurveysOptionsByLocation, getSurveysOptionsByBrand } = useSurveysOptions();

    const surveyEnabled = watch('surveyEnabled');
    const locationType = watch('locationType');
    const brandId = watch('brandId');
    const locationId = watch('locationId');
    const selectedSurveyTemplateId = watch('surveyTemplateId');

    const surveysOptions = useMemo(() => {
        return locationType === LOCATION_TYPE.BRAND
            ? getSurveysOptionsByBrand(brandId)
            : getSurveysOptionsByLocation(locationId);
    }, [locationType, brandId, locationId]);

    useEffect(() => {
        if ((locationId || brandId) && selectedSurveyTemplateId) {
            if (!surveysOptions.find(s => s.value === selectedSurveyTemplateId)) {
                setValue('surveyTemplateId', '', { shouldValidate: true });
            }
        }
    }, [selectedSurveyTemplateId, surveysOptions, locationId, brandId]);

    return (
        <div>
            <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                {t('Agents.satisfactionSurvey')}
            </Typography>
            <Controller
                control={control}
                name="surveyEnabled"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={getValues('surveyEnabled')}
                        handleChange={event => {
                            setValue('surveyEnabled', event.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                            if (!event.target.checked) {
                                setValue('surveyTemplateId', '', { shouldValidate: true });
                            }
                        }}
                        label={t('Agents.satisfactionSurveySwitch')}
                        disabled={isView}
                    />
                )}
            />
            {surveyEnabled && (
                <Controller
                    control={control}
                    name="surveyTemplateId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('surveyTemplateId', value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            options={surveysOptions}
                            label={t('common.survey')}
                            fullWidth
                            required
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            style={{
                                width: isMobile ? '100%' : 'calc(50% - 12px)',
                                marginTop: '8px',
                            }}
                        />
                    )}
                />
            )}
        </div>
    );
};

export default Survey;
